// V17.348: the on-screen dark-mode control shared by both wall displays.
//
// Extracted rather than pasted into each board for the same reason
// `utils/teamBoardTv.ts` exists: a duplicated control means a fix lands on
// one wall display and not the other, silently, on a screen nobody is
// watching. The boards' own JSX stays per-board — it diverges by segment and
// carries Chrome-69 workarounds — but this button is identical chrome on
// both, so forking it would buy nothing.

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTeamBoardStrings } from '../../utils/teamBoardI18n';

/**
 * Writes `?theme=` rather than calling `setTheme` directly.
 *
 * Both boards already run an effect that applies the URL's `?theme=` on
 * mount and whenever it changes — the URL is how a keyboard-less TV is
 * configured, so it is the source of truth. A direct `setTheme` here would
 * be reverted the moment that effect re-fires, and would not survive the
 * reload a wall TV performs after a power cut.
 *
 * `replace` keeps flips out of history, so an in-app viewer's Back button
 * still leaves the board in one press rather than unwinding every toggle.
 */
export default function TeamBoardThemeToggle() {
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useTeamBoardStrings();

  const themeParam = searchParams.get('theme')?.toLowerCase();
  const isDark = themeParam === 'dark';
  const label = isDark ? t.switchToLight : t.switchToDark;

  const toggle = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set('theme', isDark ? 'light' : 'dark');
    setSearchParams(next, { replace: true });
  }, [isDark, searchParams, setSearchParams]);

  return (
    /* Deliberately subdued and icon-only. A wall TV has no pointer to press
       it with, so on the display it is chrome that must not compete with the
       numbers; the people who actually use it are viewing this same URL
       in-app via the "TV Mode" button.

       `ml-6`, not a flex `gap` — the Chrome 69 wall TVs ignore gap on flex
       containers (it needs Chrome 84), the same reason the header's other
       blocks use margins. */
    <button
      type="button"
      data-testid="theme-toggle"
      onClick={toggle}
      aria-label={label}
      title={label}
      className="ml-6 rounded-lg border-2 border-gray-300 px-3 py-2 text-2xl
                 leading-none text-gray-500 hover:bg-gray-100
                 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-900"
    >
      {isDark ? '☀' : '☾'}
    </button>
  );
}
